import { memo } from 'react';
import { classNames } from '~/utils/classNames';
import { companyLinks } from '~/utils/footerLinks';
import { resourcesLinks } from '~/utils/resources-links';
import { socialLinks } from '~/utils/socialLinks';

type FooterLink = {
  title: string;
  items: Array<{
    text: string;
    href: string;
    showArrow?: boolean;
    icon?: string;
    displayInHeader?: boolean;
  }>;
};

const LINKS: Array<FooterLink> = [
  {
    title: 'Resources',
    items: resourcesLinks.map((item) => ({ text: item.shortName, href: item.href, showArrow: true })),
  },
  { ...companyLinks },
  {
    title: 'Social',
    items: socialLinks.map((item) => ({ text: item.name, href: item.href, icon: item.icon })),
  },
];

const CopyrightNode = (
  <span className="text-xs text-bolt-elements-textSecondary">
    &copy; {new Date().getFullYear()} StackBlitz - All rights reserved.
  </span>
);

export const FooterHomepage = () => {
  return (
    <>
      <footer className="z-layer-1 text-sm flex flex-col lg:gap-19 gap-8 border-bolt-elements-borderColor border-t lg:mt-30 sm:mt-16 mt-12 py-8">
        <div className="w-full lg:max-w-[65rem] grid grid-cols-[1fr] lg:grid-cols-[15rem_auto] mx-auto gap-8 px-5 md:px-8 xl:px-0">
          <div className="flex flex-col justify-between">
            <span className="i-bolt:logos-bolt?mask w-[56px] h-6 inline-block" />
          </div>
          <nav className="grid grid-cols-[repeat(auto-fit,minmax(7.5rem,1fr))] gap-8 lg:gap-10">
            {LINKS.map(({ title, items }, index) => (
              <LinksSection key={index} title={title} items={items} />
            ))}
          </nav>
        </div>
        <p className="text-center inline">{CopyrightNode}</p>
      </footer>
    </>
  );
};

FooterHomepage.displayName = 'FooterHomepage';

const LinksSection = memo(({ title, items }: FooterLink) => {
  return (
    <section className="text-sm flex flex-col gap-2">
      <h3>{title}</h3>
      <ul className="gap-3 flex flex-col">
        {items.map(({ text, href, showArrow, icon }, index) => (
          <li key={`${text.replace(/\s+/g, '-')}_${index}`}>
            <a
              href={href}
              className="group text-bolt-ds-textTertiary hover:text-bolt-elements-textPrimary transition-theme flex items-center gap-1.5"
              target="_blank"
            >
              {icon && (
                <span
                  className={classNames(
                    icon,
                    'text-bolt-elements-textSecondary size-4 group-hover:text-bolt-elements-textPrimary',
                  )}
                />
              )}
              {text}
              {showArrow && (
                <span className="i-lucide:arrow-up-right size-4 mt-0.5 text-bolt-elements-textSecondary group-hover:text-bolt-elements-textPrimary" />
              )}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
});

LinksSection.displayName = 'FooterLinksSection';
